import { Box, useMantineTheme } from '@mantine/core';
import type { LucideProps } from 'lucide-react';
import { HelpCircle } from 'lucide-react';
import styles from './icon.module.css';
import { iconRegistry } from './mapMaterialToLucideIcons';

const SIZE_MAP = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
} as const;

type SizeToken = keyof typeof SIZE_MAP;
type AnySize = number | string;

/**
 * IconInput can be either:
 * - a string key from our `iconRegistry` (mapped to Lucide icons)
 * - a direct Lucide icon component passed in by the consumer
 */
type IconInput = keyof typeof iconRegistry | React.ComponentType<LucideProps>;

export type IconProps = {
  icon: IconInput;
  size?: SizeToken | AnySize;
  color?: string;
  strokeWidth?: number;
  className?: string;
  ariaLabel?: string;
};

function isCssLength(value: string): boolean {
  return /^-?\d+(\.\d+)?(px|rem|em|ch|ex|vh|vw|vmin|vmax|%)$/i.test(value.trim());
}

function resolveSize(size: SizeToken | AnySize | undefined) {
  if (size == null) return '1em'; // legacy default
  // token → px
  if (typeof size === 'string' && (size as string) in SIZE_MAP) {
    return SIZE_MAP[size as SizeToken];
  }
  // numeric string → number (px)
  if (typeof size === 'string' && /^\d+(\.\d+)?$/.test(size)) {
    return Number(size);
  }
  // number → px
  if (typeof size === 'number') return size;

  if (isCssLength(size)) return size;

  return '1em';
}

export const Icon = ({
  icon,
  size = '1em',
  color = 'currentColor',
  strokeWidth = 2,
  className,
  ariaLabel,
}: IconProps) => {
  const theme = useMantineTheme();
  let LucideIcon: React.ComponentType<LucideProps>;

  if (typeof icon === 'string') {
    const foundIcon = iconRegistry[icon];
    if (foundIcon) {
      LucideIcon = foundIcon;
    } else {
      // Fallback to HelpCircle if icon is not found in registry
      LucideIcon = HelpCircle;

      // Log warning in development mode
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `Icon "${icon}" not found in iconRegistry. Available icons: ${Object.keys(iconRegistry).join(', ')}`
        );
      }
    }
  } else {
    LucideIcon = icon;
  }

  // Resolve color from Mantine theme if "palette.shade" format (e.g., "blue.6")
  let resolvedColor = color;

  if (color?.includes('.')) {
    const [palette, shade] = color.split('.');
    if (theme.colors?.[palette]?.[Number(shade)]) {
      resolvedColor = theme.colors[palette][Number(shade)];
    }
  }

  const resolvedSize = resolveSize(size);

  return (
    <Box
      className={`${styles.iconWrapper} ${className ?? ''}`}
      aria-label={ariaLabel}
      role={ariaLabel ? 'img' : undefined}
    >
      <LucideIcon
        width={resolvedSize}
        height={resolvedSize}
        color={resolvedColor}
        strokeWidth={strokeWidth}
      />
    </Box>
  );
};

// This allows consumers to import *any* Lucide icon directly if needed:
// import { Heart } from 'design-system/icons';
export * as Icons from 'lucide-react';
